import {
  AccountInput,
  useDeferredDeepLink,
  useGrippContext,
  useSendAuthCode,
  useStorageKeys,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { string, object as yupObject } from 'yup';
import { EditProfileForm, FormContext, PhoneValidator } from '../../components';
import { Button } from '../../components/crop-circle/Button';
import { colors } from '../../components/crop-circle/tokens';

type Props = {
  onVerify: () => void;
  onBack: () => void;
};

export const RegisterForm = (props: Props) => {
  const { t } = useTranslation();
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const { registerWithAuthCode, isPending } = useSendAuthCode();
  const {
    invitationId,
    anonymousIssueIds: anonymousIssueIdsParam,
    anonymousRoutineIds: anonymousRoutineIdsParam,
  } = useDeferredDeepLink();
  const anonymousIssueIds = anonymousIssueIdsParam?.split(',').filter(Boolean);
  const anonymousRoutineIds = anonymousRoutineIdsParam
    ?.split(',')
    .filter(Boolean);

  const formSchema = yupObject({
    firstName: string().required(),
    lastName: string().optional().nullable(),
    jobTitle: string().optional().nullable(),
    email: string().email().optional().nullable().lowercase(),
    phone: PhoneValidator.optional().nullable(),
    registerType: string().required(),
  }).test('email or phone', (value) => !!(value.phone || value.email));

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      email: '',
      phone: null,
      registerType: 'email',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue,
  } = formMethods;

  useEffect(() => {
    const loadAuthInput = async () => {
      const storedInput = await storageHandler.getItem(
        storageKeys.authCodeInput
      );

      if (storedInput && JSON.parse(storedInput)?.email) {
        setValue('email', JSON.parse(storedInput).email.toLowerCase());
      }
      if (storedInput && JSON.parse(storedInput)?.phone) {
        setValue('phone', JSON.parse(storedInput).phone);
        setValue('registerType', 'phone');
      }
    };

    loadAuthInput();
  }, [setValue, storageHandler, storageKeys.authCodeInput]);

  const onSubmit = async (data: any) => {
    const input = {
      name: {
        first: data.firstName,
        last: data.lastName,
      },
      phone: data.phone,
      email: data.email,
      jobTitle: data.jobTitle,
      registerType: data.registerType,
      ...(invitationId && { invitationId }),
      ...(anonymousIssueIds?.length && { anonymousIssueIds }),
      ...(anonymousRoutineIds?.length && { anonymousRoutineIds }),
    } as AccountInput;

    await registerWithAuthCode(input, props.onVerify);
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        {Platform.OS !== 'web' && (
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{t('login.aboutYourself')}</Text>
            <Pressable onPress={props.onBack}>
              <Text style={styles.headerSubtitle}>
                {t('login.existingAccount')}
              </Text>
            </Pressable>
          </View>
        )}
        <EditProfileForm control={control} autoFocus />
        <Button
          variant="rectangular"
          size="large"
          state={isDirty && isValid ? 'active' : 'default'}
          disabled={!(isDirty && isValid)}
          loading={isPending}
          onPress={handleSubmit(onSubmit)}
          style={styles.button}
        >
          {t('login.continue')}
        </Button>
      </View>
    </FormContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 24,
    ...(Platform.OS !== 'web' ? { padding: 16 } : {}),
  },
  header: {
    alignSelf: 'stretch',
    gap: 4,
  },
  headerTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'left',
    color: colors.neutral[800],
  },
  headerSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'left',
    color: colors.neutral[800],
  },
  button: {
    width: '100%',
  },
});
